import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Container, Row, Col, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import FormatDateTime from 'components/common/FormatDateTime';
import { getEventInstanceStatus, EventInstanceStatus } from 'api/events/operations/getEventInstanceStatus';

import { RootState } from '../../../../redux/store';
import { fetchEvents, Event } from '../../../../redux/slices/eventsSlice';

import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { isFileEventType } from 'utils/eventTypes';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

const toActionEntity = (row: any): ActionEntity => ({ type: 'event', id: row.event_id, zoneName: row.zone_name ?? '', name: row.name, eventType: row.type });

const ListEvents = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    items: events,
    status,
    error,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    config: headerConfig,
    filtersConfig,
    loading,
  } = useSelector((state: RootState) => state.events);

  const userData = useSelector((state: RootState) => state.auth.userData);

  const [showSidebar, setShowSidebar] = useState(false);

  const pageName = 'events';

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updateUserSettings,
    updatePage,
    updateLimit,
    limit,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: fetchEvents,
    columnFormats: { created_at: 'datetime' },
  });

  const isTableLoading = loading.fetchEvents;

  // Per-event instance status (raised / total / last-raised), fetched lazily
  // from a dedicated endpoint for just the visible page whenever the list
  // changes. Kept in local state — it's ephemeral and page-scoped, not shared,
  // and intentionally left out of the events slice (see #757 cleanup).
  const [instanceStatus, setInstanceStatus] = useState<Map<number, EventInstanceStatus>>(new Map());

  useEffect(() => {
    const ids = events.map((e) => e.event_id).filter((id): id is number => Number.isFinite(id));
    if (ids.length === 0) {
      setInstanceStatus(new Map());
      return;
    }
    let cancelled = false;
    getEventInstanceStatus(ids)
      .then((map) => {
        if (!cancelled) setInstanceStatus(map);
      })
      .catch((err) => {
        console.error('Failed to load event instance status:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [events]);

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh, navigate });
      return <ActionDropdown actions={actions} items={['edit', 'raiseEvent', 'showLive', 'showHistory', 'addToTransportEvent', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const statusColumn = {
    id: 'instance_status',
    header: 'Status',
    cell: ({ row }: any) => {
      const rowData: Event = row.original;
      const status = instanceStatus.get(rowData.event_id);
      if (!status) {
        return <span className="text-muted">…</span>;
      }
      // File events have a "processed" lifecycle; process events a "raised" one.
      const isFile = isFileEventType(rowData.type);
      const activeLabel = isFile ? 'Pending' : 'Raised';
      const idleLabel = isFile ? 'Idle' : 'Not raised';
      return (
        <div className="d-flex flex-column gap-1">
          <Badge bg={status.active ? 'success' : 'secondary'} className="align-self-start">
            {status.active ? activeLabel : idleLabel}
          </Badge>
          <small className="text-muted">
            {status.total} total
            {status.lastActiveAt ? (
              <>
                {' · last '}
                <FormatDateTime value={status.lastActiveAt} />
              </>
            ) : (
              ''
            )}
          </small>
        </div>
      );
    },
  };

  const columns = [...baseColumns, folderColumn, statusColumn, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(events), [events]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row: any) => String(row.event_id),
  });

  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh, navigate });

  return (
    <Container fluid className="p-0 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <Row className="m-0 flex-column flex-grow-1" style={{ minHeight: 0 }}>
        <Col className="p-0 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
          {error && status === 'failed' && (
            <Alert variant="danger" className="flex-shrink-0">
              {t('error-generic')}
            </Alert>
          )}

          <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
            <ActionButton actions={toolbarActions} action="createEvent" variant="primary" />
          </div>

          <TableViewLayout
            filtersConfig={filtersConfig}
            filters={filters}
            pageName={pageName}
            columns={headerConfig.map((c: any) => ({
              key: c.header_key,
              label: c.label,
            }))}
            pageSize={limit}
            error={error}
            onFilterChange={setFilterAndSync}
            onResetFilters={resetFilters}
            onPageSizeChange={updateLimit}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={updatePage}
            table={table}
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            onRefresh={refresh}
            topScrollBar
            isLoading={isTableLoading}
            isSilentRefresh={isSilentRefresh}
            toolbarButtons={<ActionButton actions={toolbarActions} action="addToTransportEvent" variant="outline-primary" label="Add selected to transport" />}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default ListEvents;
